import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { LicenceTemplateApplicationProcessAddIntelligenceReport } from '../LicenceTemplateApplicationProcessAddIntelligenceReport';
import Button from 'components/atoms/Button';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { useParams } from 'react-router-dom';
import { EPerPages, ISortOptions } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { Pagination } from 'components/molecules/Pagination';
import {
  ApplicationProcessIntelligenceReportTableColumns,
  mapApplicationProcessIntelligenceReport,
} from '../helper';
import { CardList } from 'components/molecules/CardList';
import { FormModal } from 'components/molecules/Modal';
import { EPermission } from 'models/permissions';
import ApplicationProcessIntelligenceReportTableCard from '../ApplicationProcessIntelligenceReportTableCard';

const LicenceTemplateApplicationProcessIntelligenceReports = () => {
  const { t } = useTranslations();
  const {
    handleGetapplicationProccessIntelligenceReport,
    applicationProccessIntelligenceReports,
    handleDeleteApplicationProccessIntelligenceReport,
    paginatorApplicationProccessIntelligenceReports,
    loadDictionaries,
    intelligenceReportSourceSystem,
  } = useConfigurationLicence();
  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const { processId = '' } = useParams<{ processId: string }>();
  const { clearAlert, intelligenceReportAlert } = useAlert();
  const [isAddIntelligenceReportModal, setIsAddIntelligenceReportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [
    applicationProcessIntelligenceReportDelete,
    setApplicationProcessIntelligenceReportDelete,
  ] = useState<string | null>();
  const [
    applicationProcessIntelligenceReportEdit,
    setApplicationProcessIntelligenceReportEdit,
  ] = useState<string | null>();
  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.intelligenceReportSourceSystem]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await handleGetapplicationProccessIntelligenceReport(
        processId,
        currentPage,
        perPage,
        sort,
      );
      setIsLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  useEffect(
    () => () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.LONG);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const reloadList = async () => {
    await handleGetapplicationProccessIntelligenceReport(
      processId,
      currentPage,
      perPage,
      sort,
    );
  };

  return (
    <>
      {isAddIntelligenceReportModal && (
        <LicenceTemplateApplicationProcessAddIntelligenceReport
          addSuccess={reloadList}
          handleClose={() => setIsAddIntelligenceReportModal(false)}
          applicationProcessId={processId}
        />
      )}
      {applicationProcessIntelligenceReportEdit && (
        <LicenceTemplateApplicationProcessAddIntelligenceReport
          addSuccess={reloadList}
          handleClose={() => setApplicationProcessIntelligenceReportEdit(null)}
          initialValues={applicationProccessIntelligenceReports.find(
            (val) => val.uniqueId === applicationProcessIntelligenceReportEdit,
          )}
          applicationProcessId={processId}
          intelligenceReportId={applicationProcessIntelligenceReportEdit}
        />
      )}
      <Accordion
        title={t('licence.intelligenceReports.label')}
        name="intelligenceReports"
        key="intelligenceReports"
        alwaysExpanded
        hideExpandIcon
      >
        <Grid container sx={{ justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Grid sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              label={t('licence.addIntelligenceReport.label')}
              id="addIntelligenceReports"
              variant={EButtonVariants.contained}
              onClick={() => setIsAddIntelligenceReportModal(true)}
              icon={EIconTypes.plus}
              permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
              size="medium"
              fullWidth
              sx={{ minWidth: { sm: '120px' } }}
            />
          </Grid>
        </Grid>
        <FormModal
          open={!!applicationProcessIntelligenceReportDelete}
          onClickClose={() => {
            setApplicationProcessIntelligenceReportDelete(null);
          }}
          onClickApprove={() => {
            if (applicationProcessIntelligenceReportDelete) {
              handleDeleteApplicationProccessIntelligenceReport(
                applicationProcessIntelligenceReportDelete,
              ).then(() => reloadList());
              setApplicationProcessIntelligenceReportDelete(null);
            }
          }}
          title={t('licenceConfigurator.intelligenceReport.delete.text')}
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
        />
        {intelligenceReportAlert && (
          <Grid sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Alert
                text={intelligenceReportAlert.text}
                variant={intelligenceReportAlert.variant}
              />
            </Grid>
          </Grid>
        )}
        {isLoading ? (
          <Loader isLoading={true} />
        ) : (
          <>
            {isDesktop ? (
              <Table
                columns={ApplicationProcessIntelligenceReportTableColumns({
                  handleDelete: (id) => {
                    setApplicationProcessIntelligenceReportDelete(id);
                  },
                  handleEdit: (id) => {
                    setApplicationProcessIntelligenceReportEdit(id);
                  },
                })}
                dataSource={mapApplicationProcessIntelligenceReport(
                  applicationProccessIntelligenceReports,
                  intelligenceReportSourceSystem,
                )}
                onSort={(it) => {
                  setSort(it);
                }}
              />
            ) : (
              <CardList
                items={mapApplicationProcessIntelligenceReport(
                  applicationProccessIntelligenceReports,
                  intelligenceReportSourceSystem,
                )}
                render={(data, index) => (
                  <ApplicationProcessIntelligenceReportTableCard
                    data={data}
                    key={index}
                    handleDelete={(id: string) =>
                      setApplicationProcessIntelligenceReportDelete(id)
                    }
                    handleEdit={(id: string) =>
                      setApplicationProcessIntelligenceReportEdit(id)
                    }
                  />
                )}
              />
            )}

            {paginatorApplicationProccessIntelligenceReports && (
              <Pagination
                count={paginatorApplicationProccessIntelligenceReports?.totalElements}
                onChangePage={(page) => setCurrentPage(page)}
                current={currentPage}
                perPage={perPage}
                onChangePerPage={(value) => setPerPage(value)}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </Accordion>
    </>
  );
};

export { LicenceTemplateApplicationProcessIntelligenceReports };
